import type { Agent } from './types'
import { tidyCase } from './address'
import { formatDateLong, monthDay, monthsBetween, weeksUntil, yearsWithReal } from './format'
import { isFarewell } from './milestones'
import {
  LANE_FROM,
  REPLY_TO,
  SUPPRESSION,
  addressFor,
  mergeFieldsUsed,
  suppressedBy,
  type Lane,
} from './emailrules'

/**
 * Re-exported, not redefined.
 *
 * The sender is a Cloudflare Function and cannot import this file — see
 * `emailrules.ts` for why. Everything both sides must agree about lives there;
 * this file adds what only the browser needs. A second definition of "who gets
 * an email" is the one thing this arrangement exists to prevent.
 */
export { LANE_FROM, REPLY_TO, SUPPRESSION, addressFor, mergeFieldsUsed, suppressedBy }
export type { Lane }

/**
 * Emailing agents — the rules, with no database and no network in them.
 *
 * This is the file that decides who gets an email and what it may say. It is
 * pure on purpose: the composer, the outbox, the send path and the tests all
 * have to agree about "who", and the moment that answer exists in two places
 * they drift and somebody gets an email twice or not at all.
 *
 * THE ONE RULE EVERYTHING ELSE FOLLOWS FROM
 *
 * There are two lanes, and the line between them is **how many times a human
 * reads the outgoing message** — not how many recipients there are.
 *
 *   REVIEWED  Renee reads every message before it goes. It leaves through her
 *             own Gmail, lands in her Sent folder, and merge fields are safe
 *             here precisely because a person sees each one.
 *
 *   BLAST     She reads one message and it goes to everybody unread. **Nothing
 *             merges.** Names come from reZEN, agents type them themselves, and
 *             two dozen are in ALL CAPS or all lowercase. Fine when a human is
 *             looking. Not fine when nobody is.
 *
 * See `claude/email-scope.md` and `claude/email-lane-specs.md` for the full
 * reasoning and for what was deliberately left out.
 */

// ---------------------------------------------------------------- merge fields

/**
 * The fields a reviewed email may merge, and where each one comes from.
 *
 * `agentEntered` matters: first name, phone and addresses are typed by agents
 * into reZEN and are not reliable. They are allowed here — a human reads every
 * reviewed message — and forbidden in a blast, which is the whole point of the
 * lane split.
 */
export type MergeField = {
  key: string
  label: string
  /** Typed by the agent themselves, so it can be wrong in ways nobody predicts. */
  agentEntered: boolean
  value: (a: Agent) => string | null
}

/**
 * Names go through the same tidier the mailing labels use.
 *
 * 21 first names in this roster are ALL CAPS or all lowercase, because agents
 * type them into reZEN themselves. A human reading a reviewed email will catch
 * a wrong name; they should not also have to catch a shouted one. `tidyCase`
 * lives in `address.ts` and is what stopped the Avery labels printing
 * "ADRIAN NEDELEA" — reusing it means the letter and the email agree about how
 * somebody's name is spelled, which they would not for long as two functions.
 */
const tidyName = (v: string | null): string | null => {
  const s = (v ?? '').trim()
  return s ? tidyCase(s) : null
}

export const MERGE_FIELDS: MergeField[] = [
  { key: 'first_name', label: 'First name', agentEntered: true, value: (a) => tidyName(a.first_name) },
  { key: 'last_name', label: 'Last name', agentEntered: true, value: (a) => tidyName(a.last_name) },
  { key: 'team_name', label: 'Team', agentEntered: false, value: (a) => a.team_name },
  { key: 'primary_market', label: 'Primary market', agentEntered: false, value: (a) => a.primary_market },
  { key: 'region', label: 'Region', agentEntered: false, value: (a) => a.region },
  { key: 'license_number', label: 'Licence number', agentEntered: false, value: (a) => a.license_number },
  /*
    Dates are spelled out rather than passed through.

    These two used to hand the raw column straight to the message, so an email
    read "your licence expires 2027-02-01" — a database value in the middle of a
    sentence Renee signed. `formatDateLong` also handles the trap underneath it:
    a date-only string is midnight UTC, and rendered in Eastern time every one
    of them lands on the previous evening. Every cap reset date in this roster
    is the 1st of a month, so without that guard all 785 would have printed as
    the last day of the month before.
  */
  {
    key: 'license_expires',
    label: 'Licence expiry',
    agentEntered: false,
    value: (a) => formatDateLong(a.license_expires),
  },
  /*
    Added 2026-08-17. Month-accurate, not day-accurate: every value in the
    roster is the 1st of a month. "Your cap resets February 1, 2027" is
    therefore true as written, and wording that implies a precise mid-month
    day would not be.
  */
  {
    key: 'cap_reset_date',
    label: 'Cap resets',
    agentEntered: false,
    value: (a) => formatDateLong(a.cap_reset_date),
  },
  /*
    Added 2026-09-11, at Chris's request while reviewing the "Cap reached"
    draft against the old seed skill: the reset date above already renders
    as a full date, but nothing said how far off that still was, so it was
    being typed by hand — "eight weeks" in one draft, "two months" in the
    next, two different guesses at the same span. See `weeksUntil` in
    `format.ts` for the arithmetic and for why a companion "closings this
    cap cycle" field was ruled out the same day.
  */
  {
    key: 'weeks_to_cap_reset',
    label: 'Weeks until cap reset',
    agentEntered: false,
    value: (a) => {
      const n = weeksUntil(a.cap_reset_date)
      return n == null ? null : String(n)
    },
  },
  /*
    Added 2026-08-29. `{{years}}` was refused on 2026-08-17 for this same
    reason it still deserves care: `anniversary_year` is only known for a
    minority of the roster, and an unknown value here renders as the literal
    "{{years}}" rather than dropping the draft — the same known-but-empty
    behavior every field above has. What changed is coverage grows on its own
    now (`sync-metabase.ts`'s `planBackfill` fills `anniversary_year` from
    Metabase a little at a time, no data entry needed) and this field is wired
    into exactly one place that only ever offers it once the year is already
    on file — the "Work anniversary — with years" template in
    `milestones.ts`. Dropping `{{years}}` into any other template is still the
    same mistake it always was: it silently blanks for everyone whose year
    isn't known yet.
  */
  {
    key: 'years',
    label: 'Years with Real',
    agentEntered: false,
    value: (a) => {
      const n = yearsWithReal(a)
      return n == null ? null : String(n)
    },
  },
  /*
    Added 2026-08-30, at Chris's request: "Let's make all that information
    available for her to use if she wishes." — everything the ORIGINAL
    skill this project replaced (`agent-lifecycle-emails/SKILL.md`, and the
    placeholder copy in its `templates.md`) listed as "available
    personalization," that this database also holds and this file did not
    yet expose. That old skill had a model writing fresh prose every run,
    so it could quietly drop a sentence that did not apply; a static
    template cannot, which is exactly why every field here follows the same
    known-but-empty rule as `years` above rather than a new one each: an
    unfilled one leaves its literal `{{...}}` in the draft for Renee to see
    and either fill by hand or delete, never a silently missing sentence.

    Two things on the old skill's list are deliberately NOT here:
    - `email` — it addresses the message; it is not something you merge
      INTO one.
    - "MLS and board" — stored as `Record<string, unknown>`, not the kind
      of value that renders as clean text. Worth a real field later if a
      specific shape is wanted, not a guess now.
  */
  { key: 'phone', label: 'Phone', agentEntered: true, value: (a) => (a.phone ?? '').trim() || null },
  {
    key: 'birthday',
    label: 'Birthday (month/day)',
    agentEntered: false,
    value: (a) => monthDay(a.birthday_month, a.birthday_day),
  },
  /*
    The join date, spelled out — NOT the same thing as `{{years}}` above.
    `anniversary` only holds a full date for the minority of the roster
    whose year is known (same coverage as `{{years}}`, same reasoning).
  */
  {
    key: 'anniversary_date',
    label: 'Join date',
    agentEntered: false,
    value: (a) => formatDateLong(a.anniversary),
  },
  {
    key: 'cap_date',
    label: 'Date became capped',
    agentEntered: false,
    value: (a) => formatDateLong(a.date_became_capped),
  },
  {
    key: 'elite_date',
    label: 'Date became Elite',
    agentEntered: false,
    value: (a) => formatDateLong(a.date_became_elite),
  },
  /*
    The two derived spans the old skill computed fresh every run:
    "months from join to cap, months from cap to Elite." `months_to_cap`
    additionally needs the join YEAR, so its coverage is narrower than
    `months_to_elite`, which only needs the two dates the Aug 24 fix
    already keeps current for exactly this.
  */
  {
    key: 'months_to_cap',
    label: 'Time from joining to cap',
    agentEntered: false,
    value: (a) => monthsBetween(a.anniversary, a.date_became_capped),
  },
  {
    key: 'months_to_elite',
    label: 'Time from cap to Elite',
    agentEntered: false,
    value: (a) => monthsBetween(a.date_became_capped, a.date_became_elite),
  },
  {
    key: 'pending_transactions',
    label: 'Pending transactions',
    agentEntered: false,
    value: (a) => (a.pending_transactions == null ? null : String(a.pending_transactions)),
  },
  {
    key: 'closed_transactions',
    label: 'Closed transactions',
    agentEntered: false,
    value: (a) => (a.closed_transactions == null ? null : String(a.closed_transactions)),
  },
  {
    key: 'active_listings',
    label: 'Active listings',
    agentEntered: false,
    value: (a) => (a.active_listings == null ? null : String(a.active_listings)),
  },
  {
    key: 'network_size',
    label: 'Revenue-share network size',
    agentEntered: false,
    value: (a) => (a.network_size == null ? null : String(a.network_size)),
  },
  /*
    Free text the broker wrote about the agent, not a database fact — long
    and uneven the way agent-entered names are, same reason this counts as
    `agentEntered`. Reviewed-lane only, same as everything else on this
    list: a person reads it before it goes.
  */
  { key: 'bio', label: 'Bio', agentEntered: true, value: (a) => (a.bio ?? '').trim() || null },
]

export const MERGE_BY_KEY = new Map(MERGE_FIELDS.map((f) => [f.key, f]))

/**
 * Fill in the merge fields for one agent.
 *
 * Returns the text **and** the fields that had nothing to put in them, because
 * "Hi ," is the failure this is here to prevent and it has to be visible before
 * the message goes rather than after.
 */
export function render(
  text: string,
  agent: Agent,
): { text: string; missing: string[] } {
  const missing: string[] = []
  const out = text.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (whole, key: string) => {
    const field = MERGE_BY_KEY.get(key)
    if (!field) return whole
    const value = field.value(agent)
    if (!value) {
      missing.push(key)
      return whole
    }
    return value
  })
  return { text: out, missing: [...new Set(missing)] }
}

// ------------------------------------------------------------------ recipients

export type Recipient = {
  agent: Agent
  email: string
}

export type AudienceProblem =
  | { kind: 'no-email'; agents: Agent[] }
  | { kind: 'suppressed'; tag: string; agents: Agent[] }
  | { kind: 'departed'; agents: Agent[] }
  | { kind: 'missing-merge'; field: string; agents: Agent[] }

export type Audience = {
  /**
   * Null while the composer is still on the WHO screen.
   *
   * Suppression is per-lane — `no-outreach` stops a milestone, `no-bulk-email`
   * stops a broadcast — so before she has chosen how to send, there is no
   * correct tag rule to apply. The count used to be worked out under the blast
   * rule and then relabelled, which meant the WHO screen could say 41 and the
   * confirm screen 43 with nothing in between explaining the two.
   */
  lane: Lane | null
  /** Who will actually be emailed. */
  recipients: Recipient[]
  /** Everybody who was asked for but will not be emailed, and why. */
  problems: AudienceProblem[]
  /** How many were asked for in the first place. */
  asked: number
}

/**
 * Work out who is actually going to be emailed, and say plainly who is not.
 *
 * Everything that removes somebody is reported rather than silently applied. A
 * guard that works quietly is indistinguishable from one that is not wired in —
 * the lesson the birthday guard left behind, and it applies twice as hard when
 * the thing being guarded is irreversible.
 */
export function resolveAudience(
  agents: readonly Agent[],
  lane: Lane | null,
  tagsByAgent: Map<string, string[]>,
  body = '',
  subject = '',
): Audience {
  const problems: AudienceProblem[] = []
  const departed: Agent[] = []
  const noEmail: Agent[] = []
  const suppressed = new Map<string, Agent[]>()
  const recipients: Recipient[] = []


  for (const a of agents) {
    // Departed agents never receive email. The only way one is here at all is
    // if Renee ticked them deliberately, and even then this is the safer error.
    if (!a.is_active) {
      departed.push(a)
      continue
    }
    // No lane yet means no tag rule yet — see `Audience.lane`. Departed agents
    // and missing addresses still apply, because those hold in both lanes.
    const held = lane ? suppressedBy(tagsByAgent.get(a.rezen_id) ?? [], lane) : null
    if (held) {
      const list = suppressed.get(held) ?? []
      list.push(a)
      suppressed.set(held, list)
      continue
    }
    const email = addressFor(a)
    if (!email) {
      noEmail.push(a)
      continue
    }
    recipients.push({ agent: a, email })
  }

  if (departed.length) problems.push({ kind: 'departed', agents: departed })
  for (const [tag, list] of suppressed) problems.push({ kind: 'suppressed', tag, agents: list })
  if (noEmail.length) problems.push({ kind: 'no-email', agents: noEmail })

  /**
   * A merge field with nothing behind it is a recipient problem, not a typo.
   * Eleven people getting "Hi ," under Renee's name is the failure that made
   * blasts merge-free in the first place; in the reviewed lane she can still
   * see it coming rather than meeting it one card at a time.
   */
  const used = mergeFieldsUsed(`${subject}\n${body}`)
  for (const key of used) {
    const field = MERGE_BY_KEY.get(key)
    if (!field) continue
    const blank = recipients.filter((r) => !field.value(r.agent)).map((r) => r.agent)
    if (blank.length) problems.push({ kind: 'missing-merge', field: key, agents: blank })
  }

  return { lane, recipients, problems, asked: agents.length }
}

// ------------------------------------------------------------------ validation

export type Problem = { severity: 'stop' | 'warn'; message: string }

/**
 * What is wrong with this email, in words Renee can act on.
 *
 * `stop` means the Send button is disabled. `warn` means it is her call — the
 * system does not know better than she does about her own agents, and a warning
 * she can overrule is worth more than a rule that is wrong once a year.
 */
/**
 * Fill-ins in this text that are not fields on the agent record at all.
 *
 * A KNOWN field with nothing behind it is a different thing and is only a
 * warning — `render` leaves it in place on purpose, and the reviewed lane shows
 * it to her before it goes. An UNKNOWN one can never be filled by anybody, so
 * it is a stop.
 *
 * Pulled out of `problemsWith` because the Outbox needs the same answer at a
 * different moment: the body is editable AFTER the draft was queued and
 * checked, and the only thing re-checked there used to be that it was not
 * empty. A fill-in typed on that screen went to Gmail exactly as written, on
 * the last screen before the email leaves. One function, one wording, both
 * places.
 */
export const unknownMergeFields = (text: string): string[] =>
  mergeFieldsUsed(text).filter((u) => !MERGE_BY_KEY.has(u))

/** The refusal, worded once. */
export const unknownMergeMessage = (unknown: readonly string[]): string =>
  `Nothing will fill in ${unknown.map((u) => `{{${u}}}`).join(', ')} — it is not a field on the agent record.`

export function problemsWith(opts: {
  lane: Lane
  subject: string
  body: string
  audience: Audience
}): Problem[] {
  const { lane, subject, body, audience } = opts
  const out: Problem[] = []

  if (!subject.trim()) out.push({ severity: 'stop', message: 'The subject line is empty.' })
  if (!body.trim()) out.push({ severity: 'stop', message: 'There is nothing in the message.' })
  if (!audience.recipients.length) {
    out.push({ severity: 'stop', message: 'Nobody in this list can be emailed.' })
  }

  const used = mergeFieldsUsed(`${subject}\n${body}`)

  /**
   * The rule the whole design rests on. A blast is read once by a human and
   * then goes to everyone unread, so there is nobody to catch a wrong name.
   */
  if (lane === 'blast' && used.length) {
    out.push({
      severity: 'stop',
      message:
        `This message has ${used.length === 1 ? 'a fill-in' : 'fill-ins'} in it (${used
          .map((u) => `{{${u}}}`)
          .join(', ')}). ` +
        'The same message goes to everybody here and nobody reads them one by one, ' +
        'so there is no one to notice a name that came through wrong. Take them out, ' +
        'or go back and choose to read each one.',
    })
  }

  const unknown = unknownMergeFields(`${subject}\n${body}`)
  if (unknown.length) out.push({ severity: 'stop', message: unknownMergeMessage(unknown) })

  for (const p of audience.problems) {
    if (p.kind === 'missing-merge') {
      out.push({
        severity: 'warn',
        message: `${p.agents.length} ${p.agents.length === 1 ? 'agent has' : 'agents have'} no ${MERGE_BY_KEY.get(p.field)?.label.toLowerCase() ?? p.field}, so {{${p.field}}} will be left as it is for them.`,
      })
    }
  }

  // Not a rule, a nudge. Long subjects get cut off in every mail client.
  if (subject.trim().length > 78) {
    out.push({ severity: 'warn', message: 'That subject line will be cut short in most inboxes.' })
  }

  return out
}

export const canSend = (problems: Problem[]) => !problems.some((p) => p.severity === 'stop')

// ------------------------------------------------------------- the Gmail handoff

/**
 * A Gmail compose link, pre-filled.
 *
 * This is how every reviewed email leaves: the app writes it, Gmail sends it.
 * That needs no permission from Real Brokerage's Workspace admin and no OAuth
 * app, which is why the reviewed lane works today and the blast lane needs a
 * deployed Function. See `claude/email-scope.md` — "two doors, neither ours".
 *
 * WHICH MAILBOX IT OPENS IN
 *
 * She is signed into more than one Google account, so this cannot be left to
 * chance. `/mail/u/0/` is *not* the answer — the 0 is a position in her account
 * list, and which account is at position 0 depends on the order she signed in.
 * That was here, with a comment above it claiming `authuser` was deliberately
 * absent, which is the worst of both: the wrong mailbox and a note saying it
 * had been thought about.
 *
 * `authuser=<address>` names the account instead of counting to it. If she is
 * not signed into that address Google asks her to pick, which is the right
 * failure — a chooser, not a draft sitting in the wrong Sent folder.
 *
 * THE PATH IS `/mail/u/`, NOT `/mail/`, AND THAT TURNS OUT TO MATTER.
 *
 * The first version used the bare `/mail/?authuser=…`. Renee is signed into two
 * *separate Google Workspace accounts* — her Real Brokerage one and her own
 * `reneeroark.com` one — and from the bare path Google appears to ignore the
 * parameter and open whichever is default. The documented form that honours it
 * keeps the `u/` segment with nothing after it.
 *
 * AND `authuser` IS STILL ONLY A REQUEST. THIS IS THE PART THAT BIT.
 *
 * Watched live on 2026-08-16: the app handed the browser
 * `/mail/u/?authuser=renee.roark@therealbrokerage.com&view=cm&…` and Gmail
 * answered with `/mail/u/0/?fs=1&…&tf=cm`. It **dropped the parameter** and fell
 * back to position 0. The profile was signed into `renee@reneeroark.com` only,
 * so `authuser` named an account that was not there — and rather than say so,
 * Google silently opened the one it had, pre-filled and ready to send.
 *
 * That is the worst possible failure for this lane. Everything on screen up to
 * that moment said the right address; the wrong one appears only in the Gmail
 * window, at the point where the app can no longer see anything.
 *
 * SO THE LINK GOES THROUGH THE ACCOUNT CHOOSER FIRST.
 *
 *   accounts.google.com/AccountChooser?Email=<her Real address>&continue=<compose>
 *
 * The chooser's whole job is to resolve a *named* account to a session before
 * handing on. Signed into it, she is redirected straight through and sees no
 * difference at all. Not signed into it, she gets a chooser or a sign-in page —
 * which is a stop, in front of her, before a draft exists. The failure moves out
 * of the invisible half of the handoff and into the visible one.
 *
 * `continue` carries the compose URL **unchanged**, `authuser` and all: that
 * exact shape is the one confirmed working in the right profile, and this is not
 * the moment to also change the thing that works. The chooser is a gate in front
 * of it, not a replacement for it.
 *
 * **Still not a guarantee, and it is not meant to be.** Google has never
 * promised anything about any of these URLs. The only check that survives
 * whatever Google decides is the one on the Outbox card, in the window she is
 * already looking at: read the From line before you send. This makes the right
 * mailbox likely, and a wrong one loud. It does not make the card's check
 * optional.
 */
export function gmailComposeUrl(opts: { to: string; subject: string; body: string }): string {
  const p = new URLSearchParams({
    authuser: LANE_FROM.reviewed,
    view: 'cm',
    fs: '1',
    to: opts.to,
    su: opts.subject,
    body: opts.body,
  })
  const compose = `https://mail.google.com/mail/u/?${p.toString()}`

  const chooser = new URLSearchParams({
    Email: LANE_FROM.reviewed,
    continue: compose,
  })
  return `https://accounts.google.com/AccountChooser?${chooser.toString()}`
}

/**
 * Gmail puts the whole message in the URL, and a URL has a practical ceiling
 * of a few thousand characters before browsers and Google start truncating it.
 * A milestone note is three paragraphs and nowhere near it; a newsletter pasted
 * into the wrong lane is. Better to say so than to send half an email.
 *
 * Measured against the whole link, which since the account chooser went in front
 * means the message is encoded twice — a space is `+` inside `continue` and
 * `%2B` outside it. So the effective ceiling on the message is roughly a third
 * lower than this number suggests. That is the conservative direction and it is
 * left alone deliberately: the alternative is measuring the inner URL and
 * handing the browser an outer one nobody checked.
 */
export const GMAIL_URL_LIMIT = 7000

export function gmailLinkTooLong(url: string): boolean {
  return url.length > GMAIL_URL_LIMIT
}

// ------------------------------------------------------------------- the date

/**
 * Today, as Renee's calendar has it.
 *
 * `toISOString().slice(0, 10)` is UTC. After 8pm Eastern it is already tomorrow
 * in UTC, so a campaign queued on a Sunday evening was filed under Monday — and
 * a second one queued Monday morning got a *different* id and a *different*
 * occurrence date, defeating the unique constraint that is the only thing
 * standing between one message and two. The whole guard rested on a string that
 * changed at dinner time.
 *
 * Built by hand rather than with `toLocaleDateString('en-CA')`. That reads as
 * the tidier answer and produces the same string — until it runs somewhere
 * without full ICU data, where it silently falls back to `8/15/2026`. Postgres
 * would still accept that as a date, so `occurrence_date` would be fine and
 * only the campaign **id** would change shape — which is to say the uniqueness
 * key would quietly stop matching yesterday's. Three lines of arithmetic have
 * no such day.
 */
export function localDate(at: Date): string {
  const y = at.getFullYear()
  const m = String(at.getMonth() + 1).padStart(2, '0')
  const d = String(at.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

// ------------------------------------------------------------- who a blast is for

export type AudienceRule = {
  type: 'list'
  agent_ids: string[]
  from: string
  resolved_at: string
}

/**
 * The record of who a blast is for, written into `campaigns.audience`.
 *
 * IT IS ALWAYS THE PEOPLE, BY NAME. Never a rule to be worked out again later.
 *
 * This used to store `{ type: 'all' }` whenever the group came from the filters
 * rather than from ticks. So a message composed against 41 agents with licences
 * expiring in 2027 was recorded as "everybody" — and the sender, running hours
 * later in a different process that knows nothing about her filters, would have
 * resolved that to 785. The suppression she was shown on screen would have been
 * recomputed too, against a different set of people.
 *
 * It lives here rather than in the composer because a decision this consequential
 * should be somewhere a test can reach it. It was in a JSX file, where nothing
 * could.
 */
export function blastAudienceRule(
  audience: Audience,
  origin: string,
  at: Date,
): AudienceRule {
  return {
    type: 'list',
    agent_ids: audience.recipients.map((r) => r.agent.rezen_id),
    from: origin,
    resolved_at: at.toISOString(),
  }
}

// -------------------------------------------------------- the last look before sending

export type HandoffCheck = {
  /** Non-null means do not open Gmail — say this instead. */
  block: string | null
  /** Something changed but sending is still fine. Worth saying. */
  note: string | null
  /** The address to actually use, which may not be the one on the card. */
  to: string | null
}

/**
 * Check a queued card against the roster as it stands right now.
 *
 * A batch of 52 gets worked through over days. In between, somebody leaves, or
 * replies asking not to be written to and gets tagged. The card was correct
 * when it was written and the roster is what is true now — so the roster wins,
 * and it is checked at the last possible moment rather than at queue time.
 *
 * This is the guard that makes "she can take her time with the Outbox" safe.
 */
export function recheckAtHandoff(opts: {
  agent: Agent | undefined
  tags: readonly string[]
  lane: Lane
  addressOnCard: string | null
  /**
   * The draft's own trigger, where there is one. Optional because the bulk
   * lane has no trigger at all — and absent means "an ordinary message", which
   * is the safe reading: it keeps the `is_active` guard.
   */
  trigger?: string | null
}): HandoffCheck {
  const { agent, tags, lane, addressOnCard, trigger } = opts

  if (!agent) {
    return {
      block:
        'This agent is no longer in the roster, so there is nobody to send to. Skip this one.',
      note: null,
      to: null,
    }
  }
  /**
   * …UNLESS LEAVING IS WHY WE ARE WRITING.
   *
   * This blocked on `!is_active` with no exception, so a farewell could never
   * be sent: the morning run marks the departure inactive, the draft is built,
   * Renee opens it, and this refuses it with "has left the brokerage since this
   * was queued" — which is not news about the message, it is the subject of it.
   *
   * **BE PRECISE ABOUT WHAT WAS AND WAS NOT MEASURED.** An earlier version of
   * this comment said 12 farewells had been blocked on 2026-08-20. They had
   * not. There was no `departed` template until that afternoon, so `consider`
   * declined every departure `no_template` and no draft was ever built — the
   * 12 are baseline rows in `outreach_log`, not cards in the Outbox. This was
   * the second of two gates, and the first one was shut. The template exists
   * now, which is what turns this from tidying into a fix: from the next
   * departure on, drafts are built, and without this every one of them would
   * be refused at the click.
   *
   * `isFarewell` is the engine's own predicate, imported rather than repeated,
   * because `triggerFits` makes exactly this exception at plan time and its
   * comment says out loud that this function is where the question belongs at
   * send time. It was the only half not written.
   *
   * SCOPED TO THE REVIEWED LANE. A farewell is a one-at-a-time message that
   * Renee reads before it goes; there is no such thing as a bulk farewell, and
   * `SHAPE` has no blast concept at all. Only `Outbox.tsx` calls this today and
   * it hardcodes `reviewed`, so this is a latch rather than a fix — but this
   * function is exported from the file whose whole purpose is that "may we
   * write to this person" is answered once and is safe to call from anywhere.
   *
   * Note that this skips the check in BOTH directions. A departure whose
   * `is_active` has not been flipped yet is still a departure — the evidence is
   * the Departures card, not the column, and `triggerFits` says the same. That
   * direction is not silent, though: see the note below.
   */
  const farewell = lane === 'reviewed' && isFarewell(trigger)
  if (!farewell && !agent.is_active) {
    return {
      block: `${agent.first_name ?? ''} ${agent.last_name ?? ''}`.trim() +
        ' has left the brokerage since this was queued. Skip this one.',
      note: null,
      to: null,
    }
  }
  const held = suppressedBy(tags, lane)
  if (held) {
    return {
      block: `They have been tagged "${held}" since this was queued. Skip this one.`,
      note: null,
      to: null,
    }
  }
  const now = addressFor(agent)
  if (!now) {
    return { block: 'Their email address is gone or unusable. Skip this one.', note: null, to: null }
  }
  const changed = addressOnCard && addressOnCard.toLowerCase() !== now.toLowerCase()

  /**
   * SAY SO WHEN A FAREWELL DISAGREES WITH THE ROSTER.
   *
   * Skipping the `is_active` check in both directions removed the last thing
   * standing between Renee and a "sorry you're leaving, why did you go?" email
   * to somebody the roster still shows as here. The departures card losing its
   * state filter is a real failure mode — `sync-metabase.ts` builds a whole
   * ceiling around it — and below that ceiling, one or two wrong rows is the
   * likely shape rather than a mass event.
   *
   * Not a block, because the card is the evidence and the column may simply be
   * behind. A note, because she is the one who can tell.
   */
  const notes = [
    changed ? `Their address changed since this was queued — sending to ${now}.` : null,
    farewell && agent.is_active
      ? 'The departures card listed them, but the roster still shows them as here. Worth a look before sending.'
      : null,
  ].filter((n): n is string => n !== null)

  return { block: null, note: notes.length ? notes.join(' ') : null, to: now }
}
